// financeService.js - Financial management API services
import { API_BASE, apiRequest, extractErrorMessage } from '../utils/apiUtils';

/**
 * Master data (Super-Admin-configurable option lists — unit/bag types today).
 */
export const masterDataService = {
  // List active options for a category (e.g. 'UNIT_TYPE') — any authenticated user.
  list: async (category) => {
    const response = await apiRequest(`/admin/master-data?category=${encodeURIComponent(category)}`);
    if (!response.ok) throw new Error('Failed to fetch options');
    return response.json();
  },

  // Full list including inactive — Super Admin management screen.
  listAll: async (category) => {
    const response = await apiRequest(`/admin/master-data/manage?category=${encodeURIComponent(category)}`);
    if (!response.ok) throw new Error('Failed to fetch options');
    return response.json();
  },

  create: async (data) => {
    const response = await apiRequest('/admin/master-data', { method: 'POST', body: JSON.stringify(data) });
    if (!response.ok) throw new Error('Failed to create option');
    return response.json();
  },

  update: async (id, data) => {
    const response = await apiRequest(`/admin/master-data/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!response.ok) throw new Error('Failed to update option');
    return response.json();
  },
};

/**
 * User/org/department admin API services (User Management page).
 */
export const adminService = {
  listUsers: async (organizationId, departmentId) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organizationId', organizationId);
    if (departmentId) params.set('departmentId', departmentId);
    const qs = params.toString();
    const response = await apiRequest(`/admin/users/list${qs ? `?${qs}` : ''}`);
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
  },

  setUserActive: async (id, active) => {
    const response = await apiRequest(`/admin/users/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Failed to update user');
    return data;
  },

  updateUserRole: async (id, data) => {
    const response = await apiRequest(`/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.message || 'Failed to update user role');
    return body;
  },

  updateFeatureAccess: async (id, blockedFeatures) => {
    const response = await apiRequest(`/admin/users/${id}/feature-access`, {
      method: 'PUT',
      body: JSON.stringify({ blockedFeatures }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.message || 'Failed to update feature access');
    return body;
  },

  getFormData: async () => {
    const response = await apiRequest('/admin/users/form-data');
    if (!response.ok) throw new Error('Failed to fetch form data');
    return response.json();
  },

  createUser: async (payload) => {
    const response = await apiRequest('/admin/users/create', { method: 'POST', body: JSON.stringify(payload) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Failed to create user');
    return data;
  },

  getAllOrganizations: async () => {
    const response = await apiRequest('/admin/organizations/get-all');
    if (!response.ok) throw new Error('Failed to fetch organizations');
    return response.json();
  },

  getOrganizationSummary: async (id) => {
    const response = await apiRequest(`/admin/organizations/${id}/summary`);
    if (!response.ok) throw new Error('Failed to fetch organization summary');
    return response.json();
  },

  getDepartmentsByOrganization: async (organizationId) => {
    const response = await apiRequest(`/admin/departments/by-organization/${organizationId}`);
    if (!response.ok) throw new Error('Failed to fetch departments');
    return response.json();
  },

  getMyOrganizationDepartments: async () => {
    const response = await apiRequest('/admin/departments/my-organization');
    if (!response.ok) throw new Error('Failed to fetch departments');
    return response.json();
  },
};

/**
 * Customer API services
 */
export const customerService = {
  // Get all customers
  getAll: async () => {
    const response = await apiRequest('/customers');
    if (!response.ok) throw new Error('Failed to fetch customers');
    return response.json();
  },

  // Get customers paginated
  getPaginated: async (page = 0, size = 20, sortBy = 'name', sortDir = 'asc') => {
    const response = await apiRequest(`/customers/paginated?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
    if (!response.ok) throw new Error('Failed to fetch customers');
    return response.json();
  },

  // Get customer by ID
  getById: async (id) => {
    const response = await apiRequest(`/customers/${id}`);
    if (!response.ok) throw new Error('Failed to fetch customer');
    return response.json();
  },

  // Get customers by type
  getByType: async (type) => {
    const response = await apiRequest(`/customers/by-type/${type}`);
    if (!response.ok) throw new Error('Failed to fetch customers');
    return response.json();
  },

  // Search customers
  search: async (query) => {
    const response = await apiRequest(`/customers/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) throw new Error('Failed to search customers');
    return response.json();
  },

  // Create customer
  create: async (customerData) => {
    const response = await apiRequest('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create customer');
    }
    return response.json();
  },

  // Update customer
  update: async (id, customerData) => {
    const response = await apiRequest(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update customer');
    }
    return response.json();
  },

  // Delete customer
  delete: async (id) => {
    const response = await apiRequest(`/customers/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete customer');
    return response.json();
  },

  // Get credit status for a specific customer
  getCreditStatus: async (id) => {
    const response = await apiRequest(`/customers/${id}/credit-status`);
    if (!response.ok) throw new Error('Failed to fetch credit status');
    return response.json();
  },

  // Get credit alerts for all customers
  getCreditAlerts: async () => {
    const response = await apiRequest('/customers/credit-alerts');
    if (!response.ok) throw new Error('Failed to fetch credit alerts');
    return response.json();
  }
};

/**
 * Invoice API services
 */
export const invoiceService = {
  // Get all invoices
  getAll: async () => {
    const response = await apiRequest('/invoices');
    if (!response.ok) throw new Error('Failed to fetch invoices');
    return response.json();
  },

  // Get invoices paginated
  getPaginated: async (page = 0, size = 20, sortBy = 'invoiceDate', sortDir = 'desc') => {
    const response = await apiRequest(`/invoices/paginated?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
    if (!response.ok) throw new Error('Failed to fetch invoices');
    return response.json();
  },

  // Get invoice by ID
  getById: async (id) => {
    const response = await apiRequest(`/invoices/${id}`);
    if (!response.ok) throw new Error('Failed to fetch invoice');
    return response.json();
  },

  // Get invoices by status
  getByStatus: async (status) => {
    const response = await apiRequest(`/invoices/by-status/${status}`);
    if (!response.ok) throw new Error('Failed to fetch invoices');
    return response.json();
  },

  // Get overdue invoices
  getOverdue: async () => {
    const response = await apiRequest('/invoices/overdue');
    if (!response.ok) throw new Error('Failed to fetch overdue invoices');
    return response.json();
  },

  // Get invoice statistics
  getStats: async (startDate, endDate) => {
    const response = await apiRequest(`/invoices/stats?startDate=${startDate}&endDate=${endDate}`);
    if (!response.ok) throw new Error('Failed to fetch invoice stats');
    return response.json();
  },

  // Create invoice
  create: async (invoiceData) => {
    const response = await apiRequest('/invoices', {
      method: 'POST',
      body: JSON.stringify(invoiceData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create invoice');
    }
    return response.json();
  },

  // Update invoice
  update: async (id, invoiceData) => {
    const response = await apiRequest(`/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(invoiceData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update invoice');
    }
    return response.json();
  },

  // Send invoice
  send: async (id) => {
    const response = await apiRequest(`/invoices/${id}/send`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send invoice');
    }
    return response.json();
  },

  // Record payment — payload: { amount, paymentDate, notes, paymentMode, paymentDetails }
  recordPayment: async (id, payload) => {
    const response = await apiRequest(`/invoices/${id}/payment`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to record payment');
    }
    return response.json();
  },

  // Cancel invoice
  cancel: async (id, reason) => {
    const response = await apiRequest(`/invoices/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to cancel invoice');
    }
    return response.json();
  },

  // Reopen invoice (SENT/CANCELLED → DRAFT) — admin only
  reopen: async (id) => {
    const response = await apiRequest(`/invoices/${id}/reopen`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reopen invoice');
    }
    return response.json();
  }
};

/**
 * Expense API services
 */
export const expenseService = {
  // Get all expenses (admin)
  getAll: async () => {
    const response = await apiRequest('/expenses');
    if (!response.ok) throw new Error('Failed to fetch expenses');
    return response.json();
  },

  // Get my expenses
  getMy: async () => {
    const response = await apiRequest('/expenses/my');
    if (!response.ok) throw new Error('Failed to fetch expenses');
    return response.json();
  },

  // Get expenses paginated
  getPaginated: async (page = 0, size = 20, sortBy = 'expenseDate', sortDir = 'desc') => {
    const response = await apiRequest(`/expenses/paginated?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`);
    if (!response.ok) throw new Error('Failed to fetch expenses');
    return response.json();
  },

  // Get expense by ID
  getById: async (id) => {
    const response = await apiRequest(`/expenses/${id}`);
    if (!response.ok) throw new Error('Failed to fetch expense');
    return response.json();
  },

  // Get pending expenses
  getPending: async () => {
    const response = await apiRequest('/expenses/pending');
    if (!response.ok) throw new Error('Failed to fetch pending expenses');
    return response.json();
  },

  // Get expense statistics
  getStats: async (startDate, endDate) => {
    const response = await apiRequest(`/expenses/stats?startDate=${startDate}&endDate=${endDate}`);
    if (!response.ok) throw new Error('Failed to fetch expense stats');
    return response.json();
  },

  // Get expense categories
  getCategories: async () => {
    const response = await apiRequest('/expenses/categories');
    if (!response.ok) throw new Error('Failed to fetch categories');
    return response.json();
  },

  // Create expense
  create: async (expenseData) => {
    const response = await apiRequest('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create expense');
    }
    return response.json();
  },

  // Update expense
  update: async (id, expenseData) => {
    const response = await apiRequest(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update expense');
    }
    return response.json();
  },

  // Approve expense
  approve: async (id) => {
    const response = await apiRequest(`/expenses/${id}/approve`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to approve expense');
    }
    return response.json();
  },

  // Reject expense
  reject: async (id, reason) => {
    const response = await apiRequest(`/expenses/${id}/reject?reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reject expense');
    }
    return response.json();
  },

  // Delete expense
  delete: async (id) => {
    const response = await apiRequest(`/expenses/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete expense');
    return response.json();
  }
};

/**
 * Financial Dashboard API services
 */
export const financeDashboardService = {
  // Get dashboard summary
  getDashboard: async () => {
    const response = await apiRequest('/finance/dashboard');
    if (!response.ok) throw new Error('Failed to fetch dashboard');
    return response.json();
  },

  // Get profit & loss report
  getProfitLoss: async (startDate, endDate) => {
    const response = await apiRequest(`/finance/profit-loss?startDate=${startDate}&endDate=${endDate}`);
    if (!response.ok) throw new Error('Failed to fetch P&L report');
    return response.json();
  },

  // Get trial balance
  getTrialBalance: async (asOfDate) => {
    const response = await apiRequest(`/finance/trial-balance?asOfDate=${asOfDate}`);
    if (!response.ok) throw new Error('Failed to fetch trial balance');
    return response.json();
  },

  // Get chart of accounts
  getChartOfAccounts: async () => {
    const response = await apiRequest('/finance/chart-of-accounts');
    if (!response.ok) throw new Error('Failed to fetch chart of accounts');
    return response.json();
  }
};

/**
 * Stock/Inventory API services
 */
export const stockService = {
  // Get status
  getStatus: async () => {
    const response = await apiRequest('/stock/status');
    if (!response.ok) throw new Error('Failed to fetch stock status');
    return response.json();
  },

  // Get all stock items
  getAll: async () => {
    const response = await apiRequest('/stock');
    if (!response.ok) throw new Error('Failed to fetch stock');
    return response.json();
  },

  // Get stock by ID
  getById: async (id) => {
    const response = await apiRequest(`/stock/${id}`);
    if (!response.ok) throw new Error('Failed to fetch stock item');
    return response.json();
  },

  // Get all parts
  getParts: async () => {
    const response = await apiRequest('/stock/parts');
    if (!response.ok) throw new Error('Failed to fetch parts');
    return response.json();
  },

  // Get stock locations
  getLocations: async () => {
    const response = await apiRequest('/stock/locations');
    if (!response.ok) throw new Error('Failed to fetch locations');
    return response.json();
  },

  // Get part categories
  getCategories: async () => {
    const response = await apiRequest('/stock/categories');
    if (!response.ok) throw new Error('Failed to fetch categories');
    return response.json();
  },

  // Note: addStock and removeStock are not supported in InvenTree's model.
  // In InvenTree, stock items are immutable batches/lots.
  // To add more inventory, create a NEW stock item using createStockItem() below.
  // To remove stock, use stock tracking or transfer operations in InvenTree.

  // Create a new part (product)
  createPart: async (partData) => {
    const response = await apiRequest('/stock/parts', {
      method: 'POST',
      body: JSON.stringify(partData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create part');
    }
    return response.json();
  },

  // Create a new location
  createLocation: async (locationData) => {
    const response = await apiRequest('/stock/locations', {
      method: 'POST',
      body: JSON.stringify(locationData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create location');
    }
    return response.json();
  },

  // Create a new stock item (add inventory for a part)
  createStockItem: async (stockData) => {
    const response = await apiRequest('/stock/items', {
      method: 'POST',
      body: JSON.stringify(stockData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create stock item');
    }
    return response.json();
  },

  // Create a part category
  createCategory: async (categoryData) => {
    const response = await apiRequest('/stock/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create category');
    }
    return response.json();
  }
};

/**
 * Gate Pass API services
 */
export const gatePassService = {
  getAll: async (page = 0, size = 100) => {
    const response = await apiRequest(`/gate-passes?page=${page}&size=${size}`);
    if (!response.ok) throw new Error('Failed to fetch gate passes');
    return response.json();
  },

  getById: async (id) => {
    const response = await apiRequest(`/gate-passes/${id}`);
    if (!response.ok) throw new Error('Failed to fetch gate pass');
    return response.json();
  },

  getByType: async (type) => {
    const response = await apiRequest(`/gate-passes/by-type/${type}`);
    if (!response.ok) throw new Error('Failed to fetch gate passes');
    return response.json();
  },

  // RECEIVED entry gate passes that don't already have a Purchase Order — for the "New Purchase Order" picker.
  getAvailableForPurchase: async () => {
    const response = await apiRequest('/gate-passes/available-for-purchase');
    if (!response.ok) throw new Error('Failed to fetch available gate passes');
    return response.json();
  },

  getByStatus: async (status) => {
    const response = await apiRequest(`/gate-passes/by-status/${status}`);
    if (!response.ok) throw new Error('Failed to fetch gate passes');
    return response.json();
  },

  getByDateRange: async (fromDate, toDate) => {
    const response = await apiRequest(`/gate-passes/by-date-range?fromDate=${fromDate}&toDate=${toDate}`);
    if (!response.ok) throw new Error('Failed to fetch gate passes');
    return response.json();
  },

  search: async (q, page = 0, size = 20) => {
    const response = await apiRequest(`/gate-passes/search?q=${encodeURIComponent(q)}&page=${page}&size=${size}`);
    if (!response.ok) throw new Error('Failed to search gate passes');
    return response.json();
  },

  create: async (data) => {
    const response = await apiRequest('/gate-passes', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create gate pass');
    }
    return response.json();
  },

  issue: async (id) => {
    const response = await apiRequest(`/gate-passes/${id}/issue`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to issue gate pass');
    return response.json();
  },

  cancel: async (id, reason) => {
    const response = await apiRequest(`/gate-passes/${id}/cancel?reason=${encodeURIComponent(reason)}`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to cancel gate pass');
    return response.json();
  },

  receive: async (id) => {
    const response = await apiRequest(`/gate-passes/${id}/receive`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to receive gate pass');
    return response.json();
  },

  dispatch: async (id) => {
    const response = await apiRequest(`/gate-passes/${id}/dispatch`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to dispatch gate pass');
    return response.json();
  },

  updateItemPrice: async (gatePassId, itemId, purchasePrice, purchaseCurrency) => {
    const response = await apiRequest(`/gate-passes/${gatePassId}/items/${itemId}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ purchasePrice, purchaseCurrency }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to update price');
    return data;
  }
};

/**
 * Payments, Cash Register, Checks, Pay Orders API services
 */
const buildQuery = (params) => {
  const qs = Object.entries(params || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `?${qs}` : '';
};

export const paymentsService = {
  // --- Summary / Dashboard ---
  getSummary: async () => {
    const res = await apiRequest('/payments/summary');
    if (!res.ok) throw new Error('Failed to fetch payment summary');
    return res.json();
  },

  // --- Cash Transactions ---
  getCashTransactions: async (params = {}) => {
    const res = await apiRequest(`/cash-transactions${buildQuery(params)}`);
    if (!res.ok) throw new Error('Failed to fetch cash transactions');
    return res.json();
  },

  getCashTransactionById: async (id) => {
    const res = await apiRequest(`/cash-transactions/${id}`);
    if (!res.ok) throw new Error('Failed to fetch cash transaction');
    return res.json();
  },

  createCashTransaction: async (data) => {
    const res = await apiRequest('/cash-transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create cash transaction');
    return res.json();
  },

  updateCashTransaction: async (id, data) => {
    const res = await apiRequest(`/cash-transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update cash transaction');
    return res.json();
  },

  deleteCashTransaction: async (id) => {
    const res = await apiRequest(`/cash-transactions/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete cash transaction');
    return res.json();
  },

  // --- Checks ---
  getChecks: async (params = {}) => {
    const res = await apiRequest(`/checks${buildQuery(params)}`);
    if (!res.ok) throw new Error('Failed to fetch checks');
    return res.json();
  },

  getCheckById: async (id) => {
    const res = await apiRequest(`/checks/${id}`);
    if (!res.ok) throw new Error('Failed to fetch check');
    return res.json();
  },

  getUpcomingDueChecks: async (days = 7) => {
    const res = await apiRequest(`/checks/upcoming-due?days=${days}`);
    if (!res.ok) throw new Error('Failed to fetch upcoming due checks');
    return res.json();
  },

  createCheck: async (data) => {
    const res = await apiRequest('/checks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create check');
    return res.json();
  },

  updateCheck: async (id, data) => {
    const res = await apiRequest(`/checks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update check');
    return res.json();
  },

  deleteCheck: async (id) => {
    const res = await apiRequest(`/checks/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete check');
    return res.json();
  },

  updateCheckStatus: async (id, status) => {
    const res = await apiRequest(`/checks/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error('Failed to update check status');
    return res.json();
  },

  // --- Pay Orders ---
  getPayOrders: async (params = {}) => {
    const res = await apiRequest(`/pay-orders${buildQuery(params)}`);
    if (!res.ok) throw new Error('Failed to fetch pay orders');
    return res.json();
  },

  getPayOrderById: async (id) => {
    const res = await apiRequest(`/pay-orders/${id}`);
    if (!res.ok) throw new Error('Failed to fetch pay order');
    return res.json();
  },

  createPayOrder: async (data) => {
    const res = await apiRequest('/pay-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create pay order');
    return res.json();
  },

  updatePayOrder: async (id, data) => {
    const res = await apiRequest(`/pay-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update pay order');
    return res.json();
  },

  deletePayOrder: async (id) => {
    const res = await apiRequest(`/pay-orders/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete pay order');
    return res.json();
  },

  updatePayOrderStatus: async (id, status) => {
    const res = await apiRequest(`/pay-orders/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error('Failed to update pay order status');
    return res.json();
  },
};

/**
 * GD (Goods Declaration) & Export Documents API services
 */
export const gdService = {
  getSummary: async () => {
    const res = await apiRequest('/gd/summary');
    if (!res.ok) throw new Error('Failed to fetch GD summary');
    return res.json();
  },

  getAll: async (params = {}) => {
    const res = await apiRequest(`/gd${buildQuery(params)}`);
    if (!res.ok) throw new Error('Failed to fetch GDs');
    return res.json();
  },

  getById: async (id) => {
    const res = await apiRequest(`/gd/${id}`);
    if (!res.ok) throw new Error('Failed to fetch GD');
    return res.json();
  },

  create: async (data) => {
    const res = await apiRequest('/gd', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to create GD');
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/gd/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to update GD');
    return res.json();
  },

  updateStatus: async (id, status) => {
    const res = await apiRequest(`/gd/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) });
    if (!res.ok) throw new Error('Failed to update GD status');
    return res.json();
  },

  delete: async (id) => {
    const res = await apiRequest(`/gd/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete GD');
    return res.json();
  },

  addDocument: async (gdId, data) => {
    const res = await apiRequest(`/gd/${gdId}/documents`, { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to add document');
    return res.json();
  },

  removeDocument: async (gdId, docId) => {
    const res = await apiRequest(`/gd/${gdId}/documents/${docId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to remove document');
    return res.json();
  },
};

/**
 * Exchange Rate API (USD/PKR)
 */
export const exchangeRateService = {
  getAll: async (from, to) => {
    const params = from && to ? `?from=${from}&to=${to}` : '';
    const res = await apiRequest(`/exchange-rates${params}`);
    if (!res.ok) throw new Error('Failed to fetch exchange rates');
    return res.json();
  },

  getLatest: async () => {
    const res = await apiRequest('/exchange-rates/latest');
    if (!res.ok) throw new Error('Failed to fetch latest exchange rate');
    return res.json();
  },

  getRateForDate: async (date) => {
    const res = await apiRequest(`/exchange-rates/for-date?date=${date}`);
    if (!res.ok) throw new Error('Failed to fetch rate for date');
    return res.json();
  },

  convert: async (usdAmount, date) => {
    const res = await apiRequest(`/exchange-rates/convert?usdAmount=${usdAmount}&date=${date}`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to convert');
    return res.json();
  },

  create: async (data) => {
    const res = await apiRequest('/exchange-rates', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to create exchange rate');
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/exchange-rates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to update exchange rate');
    return res.json();
  },

  delete: async (id) => {
    const res = await apiRequest(`/exchange-rates/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete exchange rate');
    return res.json();
  },
};

/**
 * Shipment Cost Sheets API
 */
export const costSheetService = {
  getAll: async () => {
    const res = await apiRequest('/cost-sheets');
    if (!res.ok) throw new Error('Failed to fetch cost sheets');
    return res.json();
  },

  getById: async (id) => {
    const res = await apiRequest(`/cost-sheets/${id}`);
    if (!res.ok) throw new Error('Failed to fetch cost sheet');
    return res.json();
  },

  create: async (data) => {
    const res = await apiRequest('/cost-sheets', { method: 'POST', body: JSON.stringify(data) });
    // These 5 all used to throw a hardcoded generic message, discarding whatever real
    // validation error the backend sent. Fixed 2026-09-23, same pass as batchService.updateStatus.
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create cost sheet'));
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/cost-sheets/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update cost sheet'));
    return res.json();
  },

  finalize: async (id) => {
    const res = await apiRequest(`/cost-sheets/${id}/finalize`, { method: 'POST' });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to finalize cost sheet'));
    return res.json();
  },

  reopen: async (id) => {
    const res = await apiRequest(`/cost-sheets/${id}/reopen`, { method: 'POST' });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to reopen cost sheet'));
    return res.json();
  },

  delete: async (id) => {
    const res = await apiRequest(`/cost-sheets/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to delete cost sheet'));
    return res.json();
  },

  addEntry: async (sheetId, data) => {
    const res = await apiRequest(`/cost-sheets/${sheetId}/entries`, { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add cost entry'));
    return res.json();
  },

  updateEntry: async (sheetId, entryId, data) => {
    const res = await apiRequest(`/cost-sheets/${sheetId}/entries/${entryId}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update cost entry'));
    return res.json();
  },

  deleteEntry: async (sheetId, entryId) => {
    const res = await apiRequest(`/cost-sheets/${sheetId}/entries/${entryId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to delete cost entry'));
    return res.json();
  },
};

/**
 * Reminders API
 */
export const reminderService = {
  getAll: async () => {
    const res = await apiRequest('/reminders');
    if (!res.ok) throw new Error('Failed to fetch reminders');
    return res.json();
  },

  getCount: async () => {
    const res = await apiRequest('/reminders/count');
    if (!res.ok) throw new Error('Failed to fetch reminder count');
    return res.json();
  },
};

/**
 * File Attachments API — generic, works for invoices, checks, cash, pay orders
 * entityType: INVOICE | CASH | CHECK | PAY_ORDER
 */
export const attachmentService = {
  upload: async (file, entityType, entityId, description) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);
    if (description) formData.append('description', description);

    const res = await fetch(`${API_BASE}/attachments/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return res.json();
  },

  list: async (entityType, entityId) => {
    const res = await apiRequest(`/attachments/${entityType}/${entityId}`);
    if (!res.ok) throw new Error('Failed to fetch attachments');
    return res.json();
  },

  delete: async (attachmentId) => {
    const res = await apiRequest(`/attachments/${attachmentId}`, { method: 'DELETE' });
    return res.json();
  },

  downloadUrl: (attachmentId) => `${API_BASE}/attachments/download/${attachmentId}`,
};

/**
 * Wastage Tracking API
 */
export const wastageService = {
  getAll: async (from, to) => {
    let url = '/wastage';
    if (from && to) url += `?from=${from}&to=${to}`;
    const res = await apiRequest(url);
    if (!res.ok) throw new Error('Failed to fetch wastage records');
    return res.json();
  },

  getSummary: async (from, to) => {
    const res = await apiRequest(`/wastage/summary?from=${from}&to=${to}`);
    if (!res.ok) throw new Error('Failed to fetch wastage summary');
    return res.json();
  },

  create: async (data) => {
    const res = await apiRequest('/wastage', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to create wastage record');
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/wastage/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to update wastage record');
    return res.json();
  },

  delete: async (id) => {
    const res = await apiRequest(`/wastage/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete wastage record');
    return res.json();
  },
};

/**
 * Product Master API
 */
export const productService = {
  getAll: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.category) searchParams.append('category', params.category);
    if (params.vendorId) searchParams.append('vendorId', params.vendorId);
    if (params.search) searchParams.append('search', params.search);
    if (params.activeOnly) searchParams.append('activeOnly', 'true');
    if (params.lowStock) searchParams.append('lowStock', 'true');
    const qs = searchParams.toString();
    const res = await apiRequest(`/products${qs ? '?' + qs : ''}`);
    if (!res.ok) throw new Error('Failed to fetch products');
    return res.json();
  },

  getById: async (id) => {
    const res = await apiRequest(`/products/${id}`);
    if (!res.ok) throw new Error('Failed to fetch product');
    return res.json();
  },

  suggestCode: async (name = '') => {
    const res = await apiRequest(`/products/suggest-code?name=${encodeURIComponent(name)}`);
    if (!res.ok) return null;
    const j = await res.json(); return j.data || null;
  },

  create: async (data) => {
    const res = await apiRequest('/products', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to create product');
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to update product');
    return res.json();
  },

  toggleActive: async (id) => {
    const res = await apiRequest(`/products/${id}/toggle-active`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to toggle product status');
    return res.json();
  },

  getCategories: async () => {
    const res = await apiRequest('/products/categories');
    if (!res.ok) throw new Error('Failed to fetch categories');
    return res.json();
  },

  adjustStock: async (id, amount, reason) => {
    const res = await apiRequest(`/products/${id}/adjust-stock`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason }),
    });
    if (!res.ok) throw new Error('Failed to adjust stock');
    return res.json();
  },

  getStockHistory: async (id) => {
    const res = await apiRequest(`/products/${id}/stock-history`);
    if (!res.ok) throw new Error('Failed to fetch stock history');
    return res.json();
  },
};

/**
 * Warehouse Management API
 */
export const warehouseService = {
  getAll: async (activeOnly = false) => {
    const res = await apiRequest(`/warehouses${activeOnly ? '?activeOnly=true' : ''}`);
    if (!res.ok) throw new Error('Failed to fetch warehouses');
    return res.json();
  },

  create: async (data) => {
    const res = await apiRequest('/warehouses', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to create warehouse');
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/warehouses/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) throw new Error('Failed to update warehouse');
    return res.json();
  },

  delete: async (id) => {
    const res = await apiRequest(`/warehouses/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete warehouse');
    return res.json();
  },

  toggle: async (id) => {
    const res = await apiRequest(`/warehouses/${id}/toggle`, { method: 'PATCH' });
    if (!res.ok) throw new Error('Failed to toggle warehouse');
    return res.json();
  },
};

/**
 * Purchase / Vendor Ledger API
 */
export const purchaseService = {
  getAll: async () => {
    const res = await apiRequest('/purchases');
    if (!res.ok) throw new Error('Failed to fetch purchases');
    return res.json();
  },

  suggestNumber: async () => {
    const res = await apiRequest('/purchases/suggest-number');
    if (!res.ok) throw new Error('Failed to suggest PO number');
    return res.text();
  },

  getByVendor: async (vendorId) => {
    const res = await apiRequest(`/purchases/by-vendor/${vendorId}`);
    if (!res.ok) throw new Error('Failed to fetch vendor purchases');
    return res.json();
  },

  getVendorLedger: async () => {
    const res = await apiRequest('/purchases/vendor-ledger');
    if (!res.ok) throw new Error('Failed to fetch vendor ledger');
    return res.json();
  },

  create: async (data) => {
    const res = await apiRequest('/purchases', { method: 'POST', body: JSON.stringify(data) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create purchase');
    }
    return res.json();
  },

  recordPayment: async (id, amount, notes, paymentDate, paymentMode, paymentDetails) => {
    const res = await apiRequest(`/purchases/${id}/payment`, {
      method: 'POST',
      body: JSON.stringify({ amount, notes, paymentDate: paymentDate || null, paymentMode: paymentMode || null, paymentDetails: paymentDetails || null }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to record payment');
    }
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiRequest(`/purchases/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update purchase');
    }
    return res.json();
  },

  cancel: async (id, reason) => {
    const res = await apiRequest(`/purchases/${id}/cancel`, {
      method: 'PATCH',
      body: JSON.stringify({ reason: reason || '' }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to cancel purchase');
    }
    return res.json();
  },
};



export const batchService = {
  getAll: async () => {
    const r = await apiRequest('/batches');
    if (!r.ok) throw new Error('Failed to fetch batches');
    return r.json();
  },
  getById: async (id) => {
    const r = await apiRequest(`/batches/${id}`);
    if (!r.ok) throw new Error('Failed to fetch batch');
    return r.json();
  },
  create: async (data) => {
    const r = await apiRequest('/batches', { method: 'POST', body: JSON.stringify(data) });
    if (!r.ok) { const e = await r.json(); throw new Error(e.message || 'Failed to create batch'); }
    return r.json();
  },
  suggestCode: async (month) => {
    const r = await apiRequest(`/batches/suggest-code?month=${month}`);
    if (!r.ok) return null;
    const j = await r.json(); return j.data || null;
  },
  importAmadFromGatePass: async (batchId, gpId) => {
    const r = await apiRequest(`/batches/${batchId}/amad/from-gate-pass/${gpId}`, { method: 'POST' });
    if (!r.ok) { const e = await r.json(); throw new Error(e.message || 'Failed to import'); }
    return r.json();
  },
  updateStatus: async (id, status) => {
    const r = await apiRequest(`/batches/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
    // Was a hardcoded generic message — discarded the backend's real reconciliation error
    // (e.g. "150 kg unaccounted for...") whenever closing an unreconciled batch. Fixed
    // 2026-09-23 to surface what the backend actually says.
    if (!r.ok) throw new Error(await extractErrorMessage(r, 'Failed to update status'));
    return r.json();
  },
  // Amad
  getAmad: async (id) => { const r = await apiRequest(`/batches/${id}/amad`); if (!r.ok) throw new Error('Failed'); return r.json(); },
  addAmad: async (id, data) => { const r = await apiRequest(`/batches/${id}/amad`, { method: 'POST', body: JSON.stringify(data) }); if (!r.ok) { const e = await r.json(); throw new Error(e.message||'Failed'); } return r.json(); },
  deleteAmad: async (batchId, entryId) => { const r = await apiRequest(`/batches/${batchId}/amad/${entryId}`, { method: 'DELETE' }); if (!r.ok) throw new Error('Failed'); return r.json(); },
  patchAmad: async (batchId, entryId, data) => { const r = await apiRequest(`/batches/${batchId}/amad/${entryId}`, { method: 'PATCH', body: JSON.stringify(data) }); if (!r.ok) { const e = await r.json(); throw new Error(e.message||'Failed'); } return r.json(); },
  // Grading
  getGrading: async (id) => { const r = await apiRequest(`/batches/${id}/grading`); if (!r.ok) throw new Error('Failed'); return r.json(); },
  addGrading: async (id, data) => { const r = await apiRequest(`/batches/${id}/grading`, { method: 'POST', body: JSON.stringify(data) }); if (!r.ok) { const e = await r.json(); throw new Error(e.message||'Failed'); } return r.json(); },
  deleteGrading: async (batchId, entryId) => { const r = await apiRequest(`/batches/${batchId}/grading/${entryId}`, { method: 'DELETE' }); if (!r.ok) throw new Error('Failed'); return r.json(); },
  // Container Costs
  getContainerCosts: async (id) => { const r = await apiRequest(`/batches/${id}/container-costs`); if (!r.ok) throw new Error('Failed'); return r.json(); },
  addContainerCost: async (id, data) => { const r = await apiRequest(`/batches/${id}/container-costs`, { method: 'POST', body: JSON.stringify(data) }); if (!r.ok) { const e = await r.json(); throw new Error(e.message||'Failed'); } return r.json(); },
  deleteContainerCost: async (batchId, entryId) => { const r = await apiRequest(`/batches/${batchId}/container-costs/${entryId}`, { method: 'DELETE' }); if (!r.ok) throw new Error('Failed'); return r.json(); },
  // Tarseel
  getTarseel: async (id) => { const r = await apiRequest(`/batches/${id}/tarseel`); if (!r.ok) throw new Error('Failed'); return r.json(); },
  addTarseel: async (id, data) => { const r = await apiRequest(`/batches/${id}/tarseel`, { method: 'POST', body: JSON.stringify(data) }); if (!r.ok) { const e = await r.json(); throw new Error(e.message||'Failed'); } return r.json(); },
  deleteTarseel: async (batchId, entryId) => { const r = await apiRequest(`/batches/${batchId}/tarseel/${entryId}`, { method: 'DELETE' }); if (!r.ok) throw new Error('Failed'); return r.json(); },
  // P&L
  getPnl: async (id) => { const r = await apiRequest(`/batches/${id}/pnl`); if (!r.ok) throw new Error('Failed'); return r.json(); },
  // Linked documents (gate passes, invoices, GDs linked to this batch)
  getLinkedDocs: async (id) => { const r = await apiRequest(`/batches/${id}/linked-docs`); if (!r.ok) throw new Error('Failed'); return r.json(); },
};

/**
 * Container payment aging — upload export-tracking Excel sheets (company log, PSW GD data),
 * matched/upserted by Container Number, aged against the 120-day export-proceeds deadline.
 */
export const containerAgingService = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/container-aging/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to upload file');
    return data;
  },
  getAll: async () => {
    const res = await apiRequest('/container-aging');
    if (!res.ok) throw new Error('Failed to fetch container aging records');
    return res.json();
  },
  getSummary: async () => {
    const res = await apiRequest('/container-aging/summary');
    if (!res.ok) throw new Error('Failed to fetch summary');
    return res.json();
  },
  remove: async (id) => {
    const res = await apiRequest(`/container-aging/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete record');
    return res.json();
  },
};
